package org.maplibre.compose.interaction.internal

import androidx.compose.ui.geometry.Offset
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.ceil
import kotlin.math.hypot
import kotlin.math.ln
import kotlin.math.pow
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds
import org.maplibre.compose.style.scaledBy
import org.maplibre.compose.style.systemAnimatorDurationScale

/** Thresholds and camera equations for [mapInput] pointer gestures. Distances are in dp. */
internal object GestureMath {
  const val PAN_START_DP = 4.0
  const val SCALE_START_SPAN_DP = 7.0
  /** Android `ViewConfiguration.getScaledDoubleTapSlop()`, used to pair two touch taps. */
  const val DOUBLE_TAP_SLOP_DP = 100.0
  const val SCALE_START_WHILE_ROTATING_DP = 75.0
  const val SHOVE_START_DP = 16.0
  const val TWO_FINGER_TAP_SLOP_DP = 5.0
  const val TWO_FINGER_TAP_TIMEOUT_MILLIS = 150L
  const val ROTATE_START_DEGREES = 3.0
  // MapLibre GL JS uses 25 logical pixels of arc travel to reject pinch-induced angle noise.
  const val ROTATE_START_WHILE_ZOOMING_ARC_DP = 25.0
  const val SHOVE_MAX_FINGER_ANGLE_DEGREES = 45.0
  const val PRESSURE_RATIO_THRESHOLD = 0.67f

  /** 6 mm at 160 dpi: 6 / 25.4 * 160. */
  const val MINIMUM_TWO_FINGER_SPAN_DP = 37.79527559055118

  private const val ZOOM_RATE = 0.65
  private const val MINIMUM_SCALE_SPEED_DP_PER_MILLISECOND = 0.6
  private const val MINIMUM_ANGLED_SCALE_SPEED_DP_PER_MILLISECOND = 0.9
  private const val MAXIMUM_SCALE_VELOCITY_ZOOM_CHANGE = 2.5
  // Decay the measured camera speed over the default momentum duration.
  private const val TRANSFORM_DECAY_MILLIS = 600.0
  // Quartic displacement gives a cubic velocity decay: a fast initial slowdown and a gentle tail.
  // Its integral is initialVelocity * duration / 4.
  const val TRANSFORM_DECAY_POWER = 4

  /** Returns a multiplicative scale, not a zoom delta. */
  fun pinchScale(rawScale: Double): Double {
    if (!rawScale.isFinite() || rawScale <= 0.0) return 1.0
    val zoomDelta = ln(rawScale) / ln(PI / 2.0) * ZOOM_RATE
    return 2.0.pow(zoomDelta)
  }

  /** Positive Y is down: dragging down zooms in and dragging up zooms out. */
  fun quickZoomDelta(
    displacementPixels: Double,
    viewportHeightPixels: Double,
    maximumZoomChange: Double,
  ): Double =
    if (viewportHeightPixels > 0.0) displacementPixels / viewportHeightPixels * maximumZoomChange
    else 0.0

  fun shouldStartScale(
    spanDeltaFromStartDp: Double,
    spanDeltaFromPreviousDp: Double,
    elapsedMillis: Long,
    rotationDeltaFromPreviousDegrees: Double,
    startSpanSlopDp: Double = SCALE_START_SPAN_DP,
  ): Boolean {
    if (abs(spanDeltaFromStartDp) < startSpanSlopDp || elapsedMillis < 0L) return false
    // Quantized synthetic samples can move at one timestamp. Their rate is unknown.
    if (elapsedMillis == 0L) return true
    val speed = abs(spanDeltaFromPreviousDp) / elapsedMillis
    if (speed < MINIMUM_SCALE_SPEED_DP_PER_MILLISECOND) return false
    return abs(rotationDeltaFromPreviousDegrees) <= 0.4 ||
      speed >= MINIMUM_ANGLED_SCALE_SPEED_DP_PER_MILLISECOND
  }

  fun shouldStartRotation(
    rotationFromStartDegrees: Double,
    rotationFromPreviousDegrees: Double,
    elapsedMillis: Long,
    startAngleDegrees: Double = ROTATE_START_DEGREES,
  ): Boolean {
    val cumulative = abs(rotationFromStartDegrees)
    if (cumulative < startAngleDegrees || elapsedMillis < 0L) return false
    if (elapsedMillis == 0L) return true
    val speed = abs(rotationFromPreviousDegrees) / elapsedMillis
    return speed >= 0.04 &&
      !(speed > 0.07 && cumulative < 5.0) &&
      !(speed > 0.15 && cumulative < 7.0) &&
      !(speed > 0.5 && cumulative < 15.0)
  }

  fun shouldStartShove(
    firstDisplacementDp: Offset,
    secondDisplacementDp: Offset,
    fingerAngleFromHorizontalDegrees: Double,
    startSlopDp: Double = SHOVE_START_DP,
  ): Boolean =
    abs((firstDisplacementDp.y + secondDisplacementDp.y) / 2) >= startSlopDp &&
      abs(firstDisplacementDp.y) > abs(firstDisplacementDp.x) &&
      abs(secondDisplacementDp.y) > abs(secondDisplacementDp.x) &&
      (firstDisplacementDp.y > 0) == (secondDisplacementDp.y > 0) &&
      abs(fingerAngleFromHorizontalDegrees) <= SHOVE_MAX_FINGER_ANGLE_DEGREES

  /** Rejects a sudden pressure drop, which is usually a finger lift. */
  fun hasStablePressure(current: Float, previous: Float): Boolean =
    previous <= 0f || current / previous > PRESSURE_RATIO_THRESHOLD

  data class Fling(
    val offsetXDp: Double,
    val offsetYDp: Double,
    val duration: Duration,
    val decayPower: Int = 2,
  ) {
    fun settleWith(transformDuration: Duration): Fling {
      // Preserve initial speed when changing decay curves, without increasing total travel.
      val distanceScale =
        minOf(1.0, transformDuration / duration * decayPower / TRANSFORM_DECAY_POWER)
      return copy(
        offsetXDp = offsetXDp * distanceScale,
        offsetYDp = offsetYDp * distanceScale,
        duration = transformDuration,
        decayPower = TRANSFORM_DECAY_POWER,
      )
    }
  }

  /**
   * Screen-space travel for a flick of this speed. Equal speeds produce equal offsets, whether or
   * not the camera is pitched. [mapInput] applies the offset in small `moveBy` steps.
   */
  fun fling(
    velocityXDpPerSecond: Double,
    velocityYDpPerSecond: Double,
    continuation: PanMomentum = PanMomentum(),
  ): Fling? {
    if (!continuation.enabled) return null
    val velocity = hypot(velocityXDpPerSecond, velocityYDpPerSecond)
    if (!velocity.isFinite() || velocity == 0.0 || velocity < continuation.minimumSpeed) return null
    val durationMillis =
      ((velocity / 10.5 + continuation.baseTime.inWholeMilliseconds) * continuation.durationScale)
        .toLong()
    if (durationMillis <= 0L || !durationMillis.milliseconds.isFinite()) return null
    return Fling(
      offsetXDp = velocityXDpPerSecond * durationMillis * 0.28 / 1000.0,
      offsetYDp = velocityYDpPerSecond * durationMillis * 0.28 / 1000.0,
      duration = durationMillis.milliseconds,
    )
  }

  /**
   * Largest screen-space `moveBy` a fling applies in one call. A dropped frame can cover the whole
   * remaining offset; splitting it keeps each unprojection as small as a live drag step.
   */
  const val FLING_MAX_STEP_DP = 16.0

  /** Splits [offsetXDp], [offsetYDp] into steps no longer than [maxStepDp]. */
  fun forEachScreenSpaceStep(
    offsetXDp: Double,
    offsetYDp: Double,
    maxStepDp: Double = FLING_MAX_STEP_DP,
    apply: (deltaX: Double, deltaY: Double) -> Unit,
  ) {
    val distance = hypot(offsetXDp, offsetYDp)
    if (distance == 0.0) return
    val steps = if (maxStepDp <= 0.0) 1 else ceil(distance / maxStepDp).toInt().coerceAtLeast(1)
    val stepX = offsetXDp / steps
    val stepY = offsetYDp / steps
    repeat(steps) { apply(stepX, stepY) }
  }

  data class ScaleVelocity(val zoomDelta: Double, val duration: Duration)

  fun scaleVelocity(
    zoomLevelsPerSecond: Double,
    continuation: VelocityMomentum = VelocityMomentum(),
  ): ScaleVelocity? {
    if (!continuation.enabled || !zoomLevelsPerSecond.isFinite() || zoomLevelsPerSecond == 0.0)
      return null
    val duration = continuation.duration(TRANSFORM_DECAY_MILLIS) ?: return null
    val zoomDelta = zoomLevelsPerSecond * duration.inWholeNanoseconds / 1e9 / TRANSFORM_DECAY_POWER
    return ScaleVelocity(
      zoomDelta.coerceIn(-MAXIMUM_SCALE_VELOCITY_ZOOM_CHANGE, MAXIMUM_SCALE_VELOCITY_ZOOM_CHANGE),
      duration,
    )
  }

  data class RotationVelocity(val bearingDelta: Double, val duration: Duration)

  fun rotationVelocity(
    degreesPerSecond: Double,
    continuation: VelocityMomentum = VelocityMomentum(),
  ): RotationVelocity? {
    if (!continuation.enabled || !degreesPerSecond.isFinite() || degreesPerSecond == 0.0)
      return null
    val duration = continuation.duration(TRANSFORM_DECAY_MILLIS) ?: return null
    return RotationVelocity(
      degreesPerSecond * duration.inWholeNanoseconds / 1e9 / TRANSFORM_DECAY_POWER,
      duration,
    )
  }

  data class TiltVelocity(val pitchDelta: Double, val duration: Duration)

  /** Integrates a pitch speed that decays cubically to zero over the configured duration. */
  fun tiltVelocity(
    degreesPerSecond: Double,
    continuation: TiltMomentum = TiltMomentum(),
  ): TiltVelocity? {
    if (
      !continuation.enabled ||
        !degreesPerSecond.isFinite() ||
        degreesPerSecond == 0.0 ||
        abs(degreesPerSecond) < continuation.minimumSpeed ||
        continuation.duration == Duration.ZERO
    )
      return null
    return TiltVelocity(
      degreesPerSecond * (continuation.duration.inWholeNanoseconds / 1e9 / TRANSFORM_DECAY_POWER),
      continuation.duration,
    )
  }

  private fun VelocityMomentum.duration(unscaledMillis: Double): Duration? {
    if (!unscaledMillis.isFinite() || durationScale == 0.0) return null
    val duration =
      (unscaledMillis.toLong().milliseconds * durationScale).coerceAtMost(maximumDuration)
    return duration.takeIf { it > Duration.ZERO }
  }
}

/** A zoom level is a doubling. */
internal fun zoomLevelsToScale(levelDelta: Double): Double = 2.0.pow(levelDelta)

internal fun InputConfiguration.scaledAnimationDuration(): Duration =
  animationDuration.scaledBy(systemAnimatorDurationScale())
